import type { Track } from './TrackLoader';

export interface Checkpoint {
  /** Index along centerline. */
  centerlineIndex: number;
  /** Position on centerline. */
  x: number;
  z: number;
  /** Forward tangent (gate normal). */
  tx: number;
  tz: number;
  /** Drivable gate extents measured from centerline. */
  widthLeft: number;
  widthRight: number;
  /** Sector this checkpoint *closes* (0..2). The finish-line closes sector 2. */
  sectorIndex: number;
  /** Distance along track at this checkpoint. */
  arcLength: number;
}

const SECTORS = 3;

export function buildCheckpoints(track: Track): Checkpoint[] {
  const out: Checkpoint[] = [];
  for (let s = 0; s < SECTORS; s++) {
    const target = s === 2 ? 0 : (track.totalLength * (s + 1)) / SECTORS;
    const i = findIndexAtArcLength(track, target);
    const c = track.centerline[i];
    const { tx, tz } = track.tangents[i];
    out.push({
      centerlineIndex: i,
      x: c.x,
      z: c.z,
      tx,
      tz,
      widthLeft: c.widthLeft,
      widthRight: c.widthRight,
      sectorIndex: s,
      arcLength: s === 2 ? track.totalLength : target,
    });
  }
  return out;
}

function findIndexAtArcLength(track: Track, target: number): number {
  const arr = track.cumulativeLength;
  let lo = 0, hi = arr.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < target) lo = mid + 1; else hi = mid;
  }
  return lo;
}

/** Tracks signed-distance to each checkpoint plane and detects ordered crossings. */
export class CheckpointTracker {
  private prevSign: number[];
  private nextIndex = 0;

  constructor(private readonly checkpoints: Checkpoint[]) {
    this.prevSign = checkpoints.map(() => 0);
  }

  reset(): void {
    this.nextIndex = 0;
    this.prevSign.fill(0);
  }

  /** Returns sectorIndex of checkpoint just crossed, or -1. */
  update(px: number, pz: number): number {
    let crossed = -1;
    for (let i = 0; i < this.checkpoints.length; i++) {
      const cp = this.checkpoints[i];
      const dx = px - cp.x;
      const dz = pz - cp.z;
      const dot = dx * cp.tx + dz * cp.tz;
      const sign = Math.sign(dot);
      if (i === this.nextIndex) {
        if (this.prevSign[i] < 0 && sign >= 0 && isInsideGate(cp, dx, dz)) {
          crossed = cp.sectorIndex;
          this.nextIndex = (this.nextIndex + 1) % this.checkpoints.length;
        }
      }
      this.prevSign[i] = sign;
    }
    return crossed;
  }
}

function isInsideGate(cp: Checkpoint, dx: number, dz: number): boolean {
  const nx = -cp.tz;
  const nz = cp.tx;
  const lateral = dx * nx + dz * nz;
  const margin = 2;
  return lateral <= cp.widthLeft + margin && lateral >= -cp.widthRight - margin;
}
